import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API_BASE = protectedResources.apiTodoList.endpoint;

function extractDomain(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  try {
    const url = s.includes("://") ? s : `https://${s}`;
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return s.replace(/^www\./, "").toLowerCase();
  }
}

// A valid domain has labels + a 2+ letter TLD (rejects "crap", "abc"). Mirrors the server check.
const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i;
function isValidDomainStr(domain) {
  return String(domain || "").length <= 253 && DOMAIN_RE.test(String(domain || ""));
}

function HorizonIgnoreDomains() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  // Stable ref — avoids re-creating fetchDomains on every render
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const { role } = useCurrentUser();
  const isAdmin = ["RISK_ADMIN", "SUPER_RISK_ADMIN"].includes((role || "").toUpperCase());

  const [domains, setDomains] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [input, setInput] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addMsg, setAddMsg] = useState({ text: "", severity: "info" });

  // Per-item delete state — only disables the chip being deleted
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, domain }
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Empty deps — stable because it uses executeRef.current, not execute directly
  const fetchDomains = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await executeRef.current(
        "GET",
        `${API_BASE}/anticipateHorizonScan/ignore-domains/list`
      );
      setDomains(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const handleAdd = async () => {
    setAddMsg({ text: "", severity: "info" });
    const raw = input.trim();
    if (!raw) return;
    const domain = extractDomain(raw);
    if (!domain) {
      setAddMsg({ text: "Could not parse a domain from the input.", severity: "warning" });
      return;
    }
    // ID113 — must be a valid domain (reject "crap", "abc", etc.) before saving.
    if (!isValidDomainStr(domain)) {
      setAddMsg({
        text: `"${domain}" is not a valid domain. Enter a real domain like "example.com".`,
        severity: "warning",
      });
      return;
    }
    setAddBusy(true);
    try {
      const res = await executeRef.current(
        "POST",
        `${API_BASE}/anticipateHorizonScan/ignore-domains/add`,
        { domain }
      );
      if (res?.success === false) {
        setAddMsg({ text: res.message || "Failed to add domain.", severity: "error" });
      } else {
        setInput("");
        setAddMsg({ text: `"${domain}" added to ignore list.`, severity: "success" });
        await fetchDomains();
      }
    } catch {
      setAddMsg({ text: "Failed to add domain.", severity: "error" });
    } finally {
      setAddBusy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    // Optimistic update — remove from UI immediately
    setDomains((prev) => prev.filter((d) => d.id !== deleteTarget.id));
    try {
      const res = await executeRef.current(
        "DELETE",
        `${API_BASE}/anticipateHorizonScan/ignore-domains/delete/${deleteTarget.id}`
      );
      if (res?.success === false) {
        // Revert optimistic update on failure
        await fetchDomains();
        setAddMsg({ text: res.message || "Failed to remove domain.", severity: "error" });
      } else {
        setAddMsg({ text: `"${deleteTarget.domain}" removed.`, severity: "success" });
      }
    } catch {
      // Revert on network error
      await fetchDomains();
      setAddMsg({ text: "Failed to remove domain.", severity: "error" });
    } finally {
      setDeleteTarget(null);
      setDeleteBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <MDTypography variant="h5" fontWeight="bold">
            Horizon Scan — Ignored Domains
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Articles from these domains will be silently dropped during news ingest. Enter a domain
            (e.g. <em>foxnews.com</em>) or paste a full URL and the domain will be extracted
            automatically.
          </MDTypography>
        </Grid>

        {loadError && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              action={
                <Button size="small" onClick={fetchDomains}>
                  Retry
                </Button>
              }
            >
              Failed to load ignore list.
            </Alert>
          </Grid>
        )}

        {addMsg.text && (
          <Grid item xs={12}>
            <Alert
              severity={addMsg.severity}
              onClose={() => setAddMsg({ text: "", severity: "info" })}
            >
              {addMsg.text}
            </Alert>
          </Grid>
        )}

        {isAdmin && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <MDTypography variant="h6" fontWeight="medium" sx={{ mb: 1 }}>
                  Add domain
                </MDTypography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={10}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. foxnews.com or https://www.foxnews.com/article"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !addBusy && handleAdd()}
                      disabled={addBusy}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={addBusy || !input.trim()}
                      onClick={handleAdd}
                      sx={{
                        bgcolor: "#52009b",
                        color: "#fff",
                        "&:hover": { bgcolor: "#3d0075" },
                        height: 40,
                      }}
                    >
                      {addBusy ? "Adding…" : "Add"}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <MDTypography variant="h6" fontWeight="medium" sx={{ mb: 1 }}>
                Ignored domains ({domains.length})
              </MDTypography>
              {domains.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No domains ignored yet.
                </MDTypography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {domains.map((d) => (
                    <Chip
                      key={d.id}
                      label={d.domain}
                      onDelete={
                        isAdmin ? () => setDeleteTarget({ id: d.id, domain: d.domain }) : undefined
                      }
                      variant="outlined"
                      color="default"
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: "#b71c1c", fontWeight: 700 }}>⚠ Caution</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            CAUTION - you are about to delete the following ignore domain{" "}
            <strong>{deleteTarget?.domain}</strong>. This may result in horizon scanning results
            being returned from this provider. Are you sure you want to proceed with the deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <MDButton onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            disabled={deleteBusy}
            onClick={handleDeleteConfirm}
          >
            {deleteBusy ? "Removing…" : "Yes, remove"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default HorizonIgnoreDomains;
